import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useT } from "../lib/i18n";

export const PageHero = ({ kicker, title, text, image, children, testid }) => {
  const t = useT();
  return (
  <section className="relative bg-[#155e3f] text-white py-20 md:py-24 overflow-hidden" data-testid={testid}>
    {image && <img src={image} alt={`${title} — Dutsen Kauna Foundation field photograph`} loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
    <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] via-[#155e3f]/85 to-[#155e3f]/40" />
    <div className="dk-container relative">
      {kicker && <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">{t(kicker)}</span>}
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 max-w-3xl">{t(title)}</h1>
      {text && <p className="mt-5 text-white text-base md:text-lg max-w-2xl leading-relaxed">{t(text)}</p>}
      {children && <div className="mt-7 flex flex-wrap gap-3">{children}</div>}
    </div>
  </section>
  );
};

export const Section = ({ kicker, title, children, className = "bg-white", id }) => {
  const t = useT();
  return (
  <section id={id} className={`py-16 md:py-20 ${className}`}>
    <div className="dk-container">
      {(kicker || title) && (
        <div className="max-w-3xl mb-10">
          {kicker && <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">{t(kicker)}</span>}
          {title && <h2 className="font-serif text-[#0a0d12] text-3xl md:text-4xl font-bold mt-3">{t(title)}</h2>}
        </div>
      )}
      {children}
    </div>
  </section>
  );
};

export const LinkCard = ({ to, title, text, testid }) => (
  <Link to={to} className="group block p-6 rounded-2xl bg-[#f8f4e8] border border-[#d6aa43]/15 hover:border-[#d6aa43]/60 hover:-translate-y-0.5 transition-all h-full" data-testid={testid}>
    <h3 className="font-serif text-xl text-[#0a0d12] font-bold">{title}</h3>
    {text && <p className="text-sm text-[#0a0d12]/70 mt-2 leading-relaxed">{text}</p>}
    <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-[#0d1724] group-hover:text-[#d6aa43]">Open <ArrowRight size={12} /></span>
  </Link>
);

export const Field = ({ label, value, onChange, type = "text", required, textarea, options, testid, placeholder }) => {
  const t = useT();
  const ph = placeholder ? t(placeholder) : undefined;
  const cls = "w-full px-4 py-3 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43] bg-white text-base";
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">{t(label)}{required ? " *" : ""}</label>
      {options ? (
        <select required={required} value={value} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testid}>
          {options.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
        </select>
      ) : textarea ? (
        <textarea required={required} rows={4} value={value} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testid} placeholder={ph} />
      ) : (
        <input required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testid} placeholder={ph} />
      )}
    </div>
  );
};

export const SerialSuccess = ({ serial, title, text, testid }) => {
  const t = useT();
  return (
  <div className="bg-white rounded-2xl p-10 text-center border border-[#d6aa43]/20 max-w-2xl mx-auto" data-testid={testid}>
    <h2 className="font-serif text-3xl text-[#0a0d12] font-bold mb-3">{t(title)}</h2>
    <p className="text-[#0a0d12]/75 mb-6">{t(text)}</p>
    <div className="p-6 rounded-xl bg-[#155e3f] text-white">
      <div className="text-xs uppercase tracking-[0.25em] text-[#d6aa43] mb-2">{t("Your reference")}</div>
      <div className="font-mono text-2xl md:text-3xl font-bold text-[#d6aa43]" data-testid={`${testid}-serial`}>{serial}</div>
    </div>
    <Link to="/" className="dk-btn-primary mt-6">{t("Return home")}</Link>
  </div>
  );
};
